import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ResolveRow } from "@/components/containers/ResolveRow";

const Box = ({ label, value, testid, warn }) => (
  <div className={`rounded-md border p-2 ${warn ? "border-terracotta/50 bg-terracotta/5" : "border-border bg-white"}`} data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</p>
    <p className="text-base font-medium text-charcoal">{value}</p>
  </div>
);

export const boxTotals = (preview, skuRes) => {
  const items = preview.unresolved_skus || [];
  const sum = (f) => items.filter(f).reduce((s, u) => s + u.boxes, 0);
  const resolved = sum((u) => skuRes[u.row]?.sku);
  const excluded = sum((u) => skuRes[u.row]?.exclude);
  return { accepted: preview.total_boxes, resolved, excluded, unresolved: sum((u) => !skuRes[u.row]), total: preview.total_boxes + (preview.unresolved_boxes || 0) };
};

// ID-067: every line without an approved SKU must be resolved (existing workflows) or explicitly excluded before confirming
export const UnresolvedSkuPanel = ({ preview, skuRes, setSkuRes, ack, setAck }) => {
  const [colors, setColors] = useState([]);
  const [designs, setDesigns] = useState([]);
  const loadDesigns = useCallback(() => api.get("/admin/catalog/designs").then(({ data }) => setDesigns(data)).catch(() => {}), []);
  useEffect(() => {
    loadDesigns();
    window.addEventListener("focus", loadDesigns);
    api.get("/products").then(({ data }) => setColors((data.products || data).filter((p) => p.color_code && p.active && !p.archived && p.family_group !== "MOSAIC_COLLECTION"))).catch(() => {});
    return () => window.removeEventListener("focus", loadDesigns);
  }, [loadDesigns]);
  const t = boxTotals(preview, skuRes);
  const items = preview.unresolved_skus || [];
  if (!items.length) return null;
  return (
    <div className="space-y-3 rounded-md border border-terracotta/40 bg-terracotta/5 p-4" data-testid="unresolved-sku-panel">
      <p className="text-xs font-semibold uppercase tracking-wider text-charcoal">Lines without an approved SKU ({items.length}) — resolve or exclude before confirming</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Box label="Accepted" value={t.accepted} testid="boxes-accepted" />
        <Box label="Resolved" value={t.resolved} testid="boxes-resolved" />
        <Box label="Unresolved" value={t.unresolved} testid="boxes-unresolved" warn={t.unresolved > 0} />
        <Box label="Excluded" value={t.excluded} testid="boxes-excluded" />
        <Box label="Total on list" value={t.total} testid="boxes-total" />
      </div>
      <ul className="space-y-2">
        {items.map((u) => (
          <ResolveRow key={`${u.row}-${u.kind}-${u.design_id || ""}`} item={u} previewId={preview.preview_id} value={skuRes[u.row]} colors={colors} designs={designs} onDesignsChanged={loadDesigns}
            onChange={(v) => { setSkuRes((r) => { const n = { ...r }; if (v) n[u.row] = v; else delete n[u.row]; return n; }); setAck(false); }} />
        ))}
      </ul>
      {t.excluded > 0 && (
        <label className="flex items-start gap-2 text-xs text-charcoal" data-testid="excluded-ack">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="excluded-ack-checkbox" />
          I understand {t.excluded} boxes will not be imported with this packing list.
        </label>
      )}
    </div>
  );
};
